import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token'));

  useEffect(() => {
    if (token) {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      // Paralelizar fetches de usuario y módulos
      Promise.all([
        axios.get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/auth/my-modules`, { headers: { Authorization: `Bearer ${token}` } })
      ])
        .then(([userRes, modulesRes]) => {
          setUser(userRes.data);
          setModules(modulesRes.data);
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = useCallback(async (email, password, { remember = true } = {}) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const res = await axios.post(`${API_BASE}/auth/login`, { email, password });
    if (remember) {
      localStorage.setItem('auth_token', res.data.access_token);
      sessionStorage.removeItem('auth_token');
    } else {
      sessionStorage.setItem('auth_token', res.data.access_token);
      localStorage.removeItem('auth_token');
    }
    setToken(res.data.access_token);
  }, []);

  const register = useCallback(async (email, password, id_perfil) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    await axios.post(`${API_BASE}/auth/register`, { email, password, id_perfil });
    // Optionally auto login
    await login(email, password);
  }, [login]);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    setModules([]);
  }, []);

  const hasModule = useCallback((moduleId) => modules.some(m => m.id === moduleId), [modules]);

  // Memoizar el valor del provider para evitar re-renders innecesarios
  const value = useMemo(
    () => ({ user, modules, loading, login, logout, register, hasModule, token }),
    [user, modules, loading, login, logout, register, hasModule, token]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}